import React, { useMemo, useState } from "react";
import { classNames } from "@via/utils/helpers.tsx";
import { Tooltip } from "react-tooltip";
import {
  CheckCircleIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  EyeIcon,
  EyeSlashIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULES,
} from "@via/utils/form-validators.tsx";
import { FieldErrors, UseFormRegister, UseFormWatch } from "react-hook-form";
import { NOTIFICATION } from "@via/context/notifications-context";
import { useNotifications } from "@via/hooks/useNotifications.tsx";

interface PasswordInputProps {
  register: UseFormRegister<any>;
  registerValue: string;
  watch: UseFormWatch<any>;
  errors: FieldErrors<any>;
  mainClass?: string;
  placeholder?: string;
  hasCopyButton?: boolean;
  focusRingWidth?: "ring-1" | "ring-2";
}

const passwordCriteriaList = [
  {
    id: "length",
    text: `${PASSWORD_MIN_LENGTH} characters minimum`,
    test: (v: string) => v.length >= PASSWORD_MIN_LENGTH,
  },
  ...PASSWORD_RULES.map(rule => ({
    id: rule.key,
    text: rule.message,
    test: (v: string) => rule.pattern.test(v),
  })),
];

const PasswordInput: React.FC<PasswordInputProps> = ({
  register,
  registerValue,
  placeholder,
  watch,
  errors,
  mainClass,
  hasCopyButton,
  focusRingWidth = "ring-2",
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const passwordValue = watch(registerValue, "");
  const { setNotification } = useNotifications();
  const passwordCriteriaMet = useMemo(() => {
    const criteria: { [key: string]: boolean } = {};
    passwordCriteriaList.forEach(c => {
      criteria[c.id] = c.test(passwordValue);
    });
    return criteria;
  }, [passwordValue]);

  const isPasswordStrong = Object.values(passwordCriteriaMet).every(Boolean);

  const handleCopyPassword = async e => {
    e.preventDefault();
    if (navigator?.clipboard) {
      if (!passwordValue) return;
      try {
        await navigator.clipboard.writeText(passwordValue);
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
      } catch (err) {
        setNotification({
          id: "copy-clipboard-not-supported-error",
          message: "Clipboard API",
          details:
            "Clipboard API not supported in this browser. Please copy the text manually or try another browser.",
          variant: NOTIFICATION.ERROR,
        });
      }
    } else {
      setNotification({
        id: "copy-clipboard-not-supported-error",
        message: "Clipboard API",
        details:
          "Clipboard API not supported in this browser. Please copy the text manually or try another browser.",
        variant: NOTIFICATION.ERROR,
      });
    }
  };

  return (
    <>
      <div className={mainClass}>
        <div>
          <label
            htmlFor="password"
            className="text-sm font-medium text-gray-700">
            Password
          </label>
          <div className="relative mt-1">
            <input
              placeholder={placeholder}
              id="password"
              type={showPassword ? "text" : "password"}
              {...register(registerValue, {
                required: "Password is required",
                validate: (value: string) => {
                  const allMet = passwordCriteriaList.every(c => c.test(value));
                  return allMet || "Password does not meet all criteria";
                },
              })}
              className={classNames(
                errors.registerValue
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                  : passwordValue &&
                      passwordCriteriaList.every(c => c.test(passwordValue))
                    ? "border-green-500 focus:ring-green-500 focus:border-green-500"
                    : "border-gray-300 focus:ring-indigo-500 focus:border-indigo-500",
                "block w-full py-2 pl-3 pr-10 text-gray-900 border rounded-md shadow-sm appearance-none focus:outline-none sm:text-sm",
                `focus:${focusRingWidth}`
              )}
            />
            <div className="absolute inset-y-0 right-0 flex items-center gap-2 pr-3">
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-tooltip-id="password-toggle-tooltip">
                {showPassword ? (
                  <EyeSlashIcon className="w-5 h-5 text-gray-500" />
                ) : (
                  <EyeIcon className="w-5 h-5 text-gray-500" />
                )}
              </button>
              {hasCopyButton && (
                <button
                  type="button"
                  onClick={handleCopyPassword}
                  data-tooltip-id="password-copy-tooltip">
                  {copied ? (
                    <CheckIcon className="w-5 h-5 text-green-500" />
                  ) : (
                    <ClipboardDocumentIcon className="w-5 h-5 text-gray-500" />
                  )}
                </button>
              )}
            </div>
          </div>
          {passwordValue && (
            <p
              className={classNames(
                isPasswordStrong ? "text-green-600" : "text-red-600",
                "mt-2 text-sm"
              )}>
              Password is {isPasswordStrong ? "strong" : "weak"}
            </p>
          )}
        </div>

        <ul className="mt-3 space-y-1">
          {passwordCriteriaList.map(criterion => {
            const isMet = passwordCriteriaMet[criterion.id];
            return (
              <li
                key={criterion.id}
                className={classNames(
                  isMet ? "text-green-600" : "text-gray-500",
                  "flex items-center text-sm"
                )}>
                {isMet ? (
                  <CheckCircleIcon className="w-4 h-4 mr-2" />
                ) : (
                  <XCircleIcon className="w-4 h-4 mr-2 text-gray-400" />
                )}
                {criterion.text}
              </li>
            );
          })}
        </ul>
      </div>
      <Tooltip
        id="password-toggle-tooltip"
        place="top"
        content={showPassword ? "Hide password" : "Show password"}
        className="z-10"
      />
      {hasCopyButton && (
        <Tooltip
          id="password-copy-tooltip"
          place="top"
          content={copied ? "Copied!" : "Copy password"}
          className="z-10"
        />
      )}
    </>
  );
};

export default PasswordInput;
